import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  ActivateRecordComposerDefinitionDocument,
  type ActivateRecordComposerDefinitionMutation,
  type ActivateRecordComposerDefinitionMutationVariables,
  DeactivateRecordComposerDefinitionDocument,
  type DeactivateRecordComposerDefinitionMutation,
  type DeactivateRecordComposerDefinitionMutationVariables,
  GetRecordComposerDefinitionDetailDocument,
  GetRecordComposerDefinitionsDocument,
  type GetRecordComposerDefinitionsQueryVariables,
} from "~/common/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";
import { useActionWithConfirm } from "~/lib/handleActionWithConfirm";

type EntityComposer = {
  id: string;
  name: string;
};

type UseEntityComposerActivationOptions = {
  listVariables?: Omit<GetRecordComposerDefinitionsQueryVariables, "tenantId">;
  canExecute?: () => boolean;
};

export function useEntityComposerActivation({ listVariables, canExecute }: UseEntityComposerActivationOptions) {
  const { t } = useTranslation();
  const handleActionWithConfirm = useActionWithConfirm();
  const { tenantId } = useTenant();
  const [deactivateRecordComposerDefinition] = useMutation<
    DeactivateRecordComposerDefinitionMutation,
    DeactivateRecordComposerDefinitionMutationVariables
  >(DeactivateRecordComposerDefinitionDocument);

  const [activateRecordComposerDefinition] = useMutation<
    ActivateRecordComposerDefinitionMutation,
    ActivateRecordComposerDefinitionMutationVariables
  >(ActivateRecordComposerDefinitionDocument);

  const onDeactivate = useCallback(
    (entityComposer: EntityComposer) => {
      if (!tenantId) {
        toast.error(translateError(t, new Error("Tenant is not resolved")));
        return;
      }

      const refetchQueries = [
        { query: GetRecordComposerDefinitionDetailDocument, variables: { tenantId, id: entityComposer.id } },
        ...(listVariables
          ? [{ query: GetRecordComposerDefinitionsDocument, variables: { tenantId, ...listVariables } }]
          : []),
      ];
      handleActionWithConfirm({
        confirmMessage: t("ui.deactivateX", { x: entityComposer.name }),
        successMessage: t("feature.common.deactivatedSuccessfully", { model: "entityComposer" }),
        action: () =>
          deactivateRecordComposerDefinition({
            refetchQueries,
            variables: { input: { tenantId, id: entityComposer.id } },
          }),
        canExecute,
      });
    },
    [handleActionWithConfirm, t, deactivateRecordComposerDefinition, tenantId, listVariables, canExecute],
  );

  const onActivate = useCallback(
    (entityComposer: EntityComposer) => {
      if (!tenantId) {
        toast.error(translateError(t, new Error("Tenant is not resolved")));
        return;
      }

      const refetchQueries = [
        { query: GetRecordComposerDefinitionDetailDocument, variables: { tenantId, id: entityComposer.id } },
        ...(listVariables
          ? [{ query: GetRecordComposerDefinitionsDocument, variables: { tenantId, ...listVariables } }]
          : []),
      ];
      handleActionWithConfirm({
        confirmMessage: t("ui.activateX", { x: entityComposer.name }),
        successMessage: t("feature.common.activatedSuccessfully", { model: "entityComposer" }),
        action: () =>
          activateRecordComposerDefinition({
            refetchQueries,
            variables: { input: { tenantId, id: entityComposer.id } },
          }),
        canExecute,
      });
    },
    [handleActionWithConfirm, t, activateRecordComposerDefinition, tenantId, listVariables, canExecute],
  );

  return { onActivate, onDeactivate };
}
